import { useCallback, useEffect, useRef, useState } from "react";
import type {
  Action,
  ClientMessage,
  RoomView,
  SeatCredentials,
  ServerMessage,
  TicketResponse,
} from "../../server/protocol";

export type { Action, RoomView, SeatCredentials } from "../../server/protocol";
export type ConnectionStatus =
  | "idle"
  | "connecting"
  | "syncing"
  | "online"
  | "reconnecting"
  | "offline"
  | "seat-moved"
  | "expired"
  | "removed"
  | "error";
export type RoomSnapshot = {
  state: RoomView | null;
  status: ConnectionStatus;
  error: string | null;
  pending: boolean;
  pendingActionIds: string[];
};
type ActionFrame = Extract<ClientMessage, { type: "action" }>;
const API_BASE = (import.meta.env?.VITE_API_URL ?? "").replace(/\/$/, "");
const SEAT_PREFIX = "after-hours:seat:v1:";
const codeKey = (code: string) => code.trim().toUpperCase();

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code = "NETWORK_ERROR",
    public readonly status = 0,
    public readonly retryable = true,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: body === undefined ? "GET" : "POST",
      headers:
        body === undefined
          ? { Accept: "application/json" }
          : { Accept: "application/json", "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
      cache: "no-store",
      credentials: "omit",
      signal,
    });
  } catch {
    throw new ApiError("Connection interrupted. Please try again.");
  }
  let data: unknown;
  try {
    data = await response.json();
  } catch {
    throw new ApiError(
      "The server could not respond. Please try again.",
      "INVALID_RESPONSE",
      response.status,
    );
  }
  if (!response.ok) {
    const value = data as {
      error?: { message?: unknown; code?: unknown; retryable?: unknown };
    };
    const message =
      typeof value?.error?.message === "string" &&
      value.error.message.length <= 240
        ? value.error.message
        : "That request could not be completed.";
    const code =
      typeof value?.error?.code === "string"
        ? value.error.code
        : "REQUEST_FAILED";
    throw new ApiError(
      message,
      code,
      response.status,
      response.status === 429 || response.status >= 500,
    );
  }
  return data as T;
}

/** No automatic POST retry: creation/join may have succeeded before a lost response. */
export async function api<T = any>(path: string, body?: unknown): Promise<T> {
  const abort = new AbortController();
  const timeout = setTimeout(() => abort.abort(), 10000);
  try {
    return await request<T>(path, body, abort.signal);
  } finally {
    clearTimeout(timeout);
  }
}

function cleanSeat(code: string, value: unknown): SeatCredentials | null {
  if (!value || typeof value !== "object") return null;
  const seat = value as Partial<SeatCredentials>;
  if (
    seat.protocolVersion !== 1 ||
    codeKey(seat.code ?? "") !== codeKey(code) ||
    typeof seat.playerId !== "string" ||
    !seat.playerId ||
    typeof seat.roomInstanceId !== "string" ||
    !seat.roomInstanceId ||
    typeof seat.token !== "string" ||
    !seat.token
  )
    return null;
  return {
    protocolVersion: 1,
    code: codeKey(code),
    roomInstanceId: seat.roomInstanceId,
    playerId: seat.playerId,
    token: seat.token,
    ...(seat.isSpectator === true ? { isSpectator: true } : {}),
  };
}

export function saveSeat(code: string, credentials: SeatCredentials): void {
  const value = cleanSeat(code, credentials);
  if (!value)
    throw new ApiError(
      "This seat could not be saved.",
      "INVALID_SEAT",
      0,
      false,
    );
  try {
    localStorage.setItem(
      `${SEAT_PREFIX}${codeKey(code)}`,
      JSON.stringify(value),
    );
  } catch {
    throw new ApiError(
      "Allow site storage to keep your seat when you return.",
      "STORAGE_UNAVAILABLE",
      0,
      false,
    );
  }
}

export function readSeat(code: string): SeatCredentials | null {
  try {
    return cleanSeat(
      code,
      JSON.parse(
        localStorage.getItem(`${SEAT_PREFIX}${codeKey(code)}`) ?? "null",
      ),
    );
  } catch {
    return null;
  }
}

export function forgetSeat(code: string): void {
  try {
    localStorage.removeItem(`${SEAT_PREFIX}${codeKey(code)}`);
  } catch {
    /* Storage may have been disabled since joining. */
  }
}
/** Every saved seat in this browser (code + credentials), for the catalogue's Resume card. */
export function listSeats(): SeatCredentials[] {
  const out: SeatCredentials[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(SEAT_PREFIX)) continue;
      const seat = readSeat(key.slice(SEAT_PREFIX.length));
      if (seat) out.push(seat);
    }
  } catch {
    /* Storage unavailable: nothing to resume. */
  }
  return out;
}
export function ensureSeatStorage(): void {
  const key = "after-hours:storage-check";
  try {
    localStorage.setItem(key, "1");
    localStorage.removeItem(key);
  } catch {
    throw new ApiError(
      "Allow site storage to keep your seat when you return.",
      "STORAGE_UNAVAILABLE",
      0,
      false,
    );
  }
}

type RoomOptions = {
  code: string;
  credentials: SeatCredentials | null;
  isDisplay?: boolean;
  onChange?: (snapshot: RoomSnapshot) => void;
};
const emptySnapshot = (): RoomSnapshot => ({
  state: null,
  status: "idle",
  error: null,
  pending: false,
  pendingActionIds: [],
});

/** Owns one browser connection. React and tests both use this same lifecycle. */
export function createRoomConnection(options: RoomOptions) {
  const code = codeKey(options.code);
  const path = `/api/rooms/${encodeURIComponent(code)}`;
  let snapshot = emptySnapshot();
  let socket: WebSocket | null = null;
  let active = false;
  let blocked = false;
  let generation = 0;
  let attempts = 0;
  let connecting = false;
  let needsReplay = false;
  let abort: AbortController | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let connectTimer: ReturnType<typeof setTimeout> | undefined;
  let heartbeatTimer: ReturnType<typeof setTimeout> | undefined;
  let pongTimer: ReturnType<typeof setTimeout> | undefined;
  let actionTimer: ReturnType<typeof setTimeout> | undefined;
  let probeTimer: ReturnType<typeof setTimeout> | undefined;
  let lastSyncAt = 0;
  const pending = new Map<string, ActionFrame>();
  const visible = () => document.visibilityState !== "hidden";
  const online = () => navigator.onLine !== false;
  const publish = (patch: Partial<RoomSnapshot>) => {
    snapshot = {
      ...snapshot,
      ...patch,
      pending: pending.size > 0,
      pendingActionIds: [...pending.keys()],
    };
    options.onChange?.(snapshot);
  };
  const clearConnectionTimers = () => {
    clearTimeout(connectTimer);
    clearTimeout(heartbeatTimer);
    clearTimeout(pongTimer);
    clearTimeout(actionTimer);
    clearTimeout(probeTimer);
    probeTimer = undefined;
  };
  /** A returning tab proved its socket is alive: resume normal service. */
  const probeAnswered = () => {
    if (probeTimer === undefined) return;
    clearTimeout(probeTimer);
    probeTimer = undefined;
    watchAction();
  };
  const closeSocket = () => {
    const previous = socket;
    socket = null;
    if (previous) {
      previous.onopen = null;
      previous.onmessage = null;
      previous.onclose = null;
      previous.onerror = null;
      previous.close();
    }
    clearConnectionTimers();
  };
  const transmit = (frame: string) => {
    if (!socket || socket.readyState !== 1) return false;
    try {
      socket.send(frame);
      return true;
    } catch {
      return false;
    }
  };
  const terminal = (status: ConnectionStatus, error: string) => {
    blocked = true;
    generation++;
    connecting = false;
    abort?.abort();
    clearTimeout(retryTimer);
    closeSocket();
    pending.clear();
    publish({ status, error, state: null });
  };
  const scheduleRetry = () => {
    if (!active || blocked) return;
    clearTimeout(retryTimer);
    publish({ status: online() ? "reconnecting" : "offline" });
    if (!online() || !visible()) return;
    const delay =
      Math.min(15000, 500 * 2 ** Math.min(attempts++, 5)) *
      (0.8 + Math.random() * 0.4);
    retryTimer = setTimeout(() => {
      void connect();
    }, delay);
  };
  const lostConnection = () => {
    if (!active || blocked) return;
    generation++;
    connecting = false;
    abort?.abort();
    closeSocket();
    needsReplay = pending.size > 0;
    publish({
      error: pending.size
        ? "Connection interrupted — checking your action."
        : null,
    });
    scheduleRetry();
  };
  const heartbeat = () => {
    clearTimeout(heartbeatTimer);
    clearTimeout(pongTimer);
    if (!active || blocked || !visible() || socket?.readyState !== 1) return;
    heartbeatTimer = setTimeout(() => {
      if (!transmit("ping")) {
        lostConnection();
        return;
      }
      pongTimer = setTimeout(lostConnection, 10000);
    }, 20000);
  };
  const watchAction = () => {
    clearTimeout(actionTimer);
    if (pending.size) actionTimer = setTimeout(lostConnection, 10000);
  };
  const receive = (data: unknown) => {
    if (data === "pong") {
      clearTimeout(pongTimer);
      probeAnswered();
      if (snapshot.status === "syncing" && snapshot.state)
        publish({ status: "online" });
      heartbeat();
      return;
    }
    if (typeof data !== "string") return;
    let message: ServerMessage;
    try {
      message = JSON.parse(data) as ServerMessage;
      if (
        !message ||
        typeof message !== "object" ||
        !["state", "ack", "error"].includes(message.type)
      )
        throw new Error("Invalid frame");
    } catch {
      terminal("error", "The room could not synchronize. Please reload.");
      return;
    }
    if (message.type === "state") {
      if (
        message.protocolVersion !== 1 ||
        message.state?.protocolVersion !== 1
      ) {
        terminal("error", "A new version is ready. Please reload.");
        return;
      }
      const next = message.state;
      if (
        next.code !== code ||
        (!options.isDisplay &&
          next.roomInstanceId !== options.credentials?.roomInstanceId)
      ) {
        terminal("expired", "This room has ended. Please join a new room.");
        return;
      }
      clearTimeout(connectTimer);
      probeAnswered();
      attempts = 0;
      publish({ state: next, status: "online", error: null });
      if (needsReplay) {
        needsReplay = false;
        for (const [id, frame] of pending) {
          if (
            frame.matchId === next.matchId &&
            frame.phaseId === next.phaseId &&
            frame.round === next.round &&
            !next.paused
          )
            transmit(JSON.stringify(frame));
          else pending.delete(id); // Never submit an old choice to a later phase.
        }
        publish({});
        watchAction();
      }
      heartbeat();
    } else if (message.type === "ack") {
      pending.delete(message.actionId);
      publish({ error: null });
      watchAction();
    } else if (message.type === "error") {
      if (message.actionId) pending.delete(message.actionId);
      const safe =
        typeof message.message === "string" && message.message.length <= 240
          ? message.message
          : "That action could not be completed.";
      if (message.code === "UPGRADE_REQUIRED")
        terminal("error", "A new version is ready. Please reload.");
      else if (message.code === "SEAT_MOVED")
        terminal("seat-moved", "This seat is open in another tab.");
      else if (
        [
          "REVOKED",
          "INVALID_SESSION",
          "INVALID_CREDENTIALS",
          "SEAT_REVOKED",
          "ROOM_EXPIRED",
          "ROOM_NOT_FOUND",
        ].includes(message.code)
      )
        terminal("expired", safe);
      else {
        publish({ error: safe });
        watchAction();
      }
    }
  };
  async function connect() {
    if (!active || blocked || connecting || !visible()) return;
    if (!online()) {
      publish({ status: "offline" });
      return;
    }
    if (!options.isDisplay && !options.credentials) {
      publish({ status: "idle", state: null });
      return;
    }
    connecting = true;
    const epoch = ++generation;
    closeSocket();
    abort?.abort();
    abort = new AbortController();
    publish({
      status: snapshot.state ? "reconnecting" : "connecting",
      error: null,
    });
    connectTimer = setTimeout(() => {
      abort?.abort();
      if (active && epoch === generation) lostConnection();
    }, 10000);
    try {
      const seat = options.credentials;
      const ticket = await request<TicketResponse>(
        `${path}/${options.isDisplay ? "display" : "session"}`,
        options.isDisplay
          ? {}
          : {
              roomInstanceId: seat!.roomInstanceId,
              playerId: seat!.playerId,
              token: seat!.token,
            },
        abort.signal,
      );
      if (!active || epoch !== generation) return;
      if (ticket.protocolVersion !== 1) {
        terminal("error", "A new version is ready. Please reload.");
        return;
      }
      if (typeof ticket.ticket !== "string" || !ticket.ticket)
        throw new ApiError("The room could not connect. Please retry.");
      const url = new URL(`${API_BASE}${path}/socket`, window.location.origin);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      url.searchParams.set("ticket", ticket.ticket);
      const current = new WebSocket(url.toString());
      socket = current;
      current.onopen = () => {
        if (active && epoch === generation) {
          connecting = false;
          publish({ status: "syncing" });
          heartbeat();
        }
      };
      current.onmessage = (event) => {
        if (active && epoch === generation) receive(event.data);
      };
      current.onerror = () => {
        if (active && epoch === generation) lostConnection();
      };
      current.onclose = (event) => {
        if (!active || epoch !== generation) return;
        if (event.code === 4001)
          terminal("seat-moved", "This seat is open in another tab.");
        else if (event.code === 4005)
          terminal("removed", "The host removed you from this room.");
        else if (event.code === 4003)
          terminal("expired", "Your access to this room has ended.");
        else if (event.code === 4004)
          terminal("expired", "This room has ended.");
        else lostConnection(); // Includes spectator promotion (4002): authenticate the same seat again.
      };
    } catch (error) {
      if (!active || epoch !== generation) return;
      connecting = false;
      if (error instanceof ApiError && !error.retryable)
        terminal(
          error.status === 404 || error.status === 401 || error.status === 403
            ? "expired"
            : "error",
          error.message,
        );
      else {
        publish({
          error:
            error instanceof ApiError
              ? error.message
              : "Connection interrupted. Please retry.",
        });
        lostConnection();
      }
    }
  }
  const retry = () => {
    if (!active) return;
    blocked = false;
    attempts = 0;
    clearTimeout(retryTimer);
    generation++;
    connecting = false;
    abort?.abort();
    closeSocket();
    needsReplay = pending.size > 0;
    void connect();
  };
  const onVisibility = () => {
    if (!active || blocked) return;
    if (!visible()) {
      clearTimeout(heartbeatTimer);
      clearTimeout(pongTimer);
      clearTimeout(actionTimer);
      clearTimeout(retryTimer);
      publish({ status: "syncing" });
      return;
    }
    // An apparently-open socket may be a suspended Safari one. Probe it: a pong or a
    // fresh state within 1.5s keeps the seat (no offline/online flicker for the
    // table); silence means it is dead, so fall back to a fresh ticket + snapshot.
    if (socket?.readyState === 1 && !connecting) {
      clearTimeout(probeTimer);
      const sent = transmit("ping");
      const now = Date.now();
      if (sent && now - lastSyncAt > 1100) {
        lastSyncAt = now;
        transmit(JSON.stringify({ type: "sync" }));
      }
      if (!sent) {
        retry();
        return;
      }
      probeTimer = setTimeout(() => {
        probeTimer = undefined;
        if (active && !blocked) retry();
      }, 1500);
      return;
    }
    retry();
  };
  const onOnline = () => {
    if (!blocked) retry();
  };
  const onOffline = () => {
    if (!blocked) {
      lostConnection();
      publish({ status: "offline" });
    }
  };
  const start = () => {
    if (active) return;
    active = true;
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("pageshow", onOnline);
    void connect();
  };
  const stop = () => {
    active = false;
    generation++;
    abort?.abort();
    clearTimeout(retryTimer);
    closeSocket();
    pending.clear();
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("online", onOnline);
    window.removeEventListener("offline", onOffline);
    window.removeEventListener("pageshow", onOnline);
  };
  const send = (action: Action): string | null => {
    const room = snapshot.state;
    if (
      !active ||
      blocked ||
      options.isDisplay ||
      snapshot.status !== "online" ||
      !visible() ||
      !room ||
      pending.size ||
      socket?.readyState !== 1
    )
      return null;
    if (room.paused && !action.type.startsWith("room.")) return null;
    const id = crypto.randomUUID();
    const frame: ActionFrame = {
      type: "action",
      protocolVersion: 1,
      id,
      matchId: room.matchId,
      phaseId: room.phaseId,
      round: room.round,
      revision: room.revision,
      action: structuredClone(action),
    };
    pending.set(id, frame);
    publish({ error: null });
    if (!transmit(JSON.stringify(frame))) lostConnection();
    else watchAction();
    return id;
  };
  return { start, stop, send, retry, getSnapshot: () => snapshot };
}

/** Primitive dependencies prevent parent renders from churning authenticated sockets. */
export function useRoom(
  code: string,
  credentials: SeatCredentials | null,
  isDisplay = false,
) {
  const [snapshot, setSnapshot] = useState<RoomSnapshot>(emptySnapshot);
  const roomRef = useRef<ReturnType<typeof createRoomConnection> | null>(null);
  const roomInstanceId = credentials?.roomInstanceId;
  const playerId = credentials?.playerId;
  const token = credentials?.token;
  useEffect(() => {
    const seat: SeatCredentials | null =
      roomInstanceId && playerId && token
        ? {
            protocolVersion: 1,
            code: codeKey(code),
            roomInstanceId,
            playerId,
            token,
          }
        : null;
    const room = createRoomConnection({
      code,
      credentials: seat,
      isDisplay,
      onChange: setSnapshot,
    });
    roomRef.current = room;
    setSnapshot(room.getSnapshot());
    room.start();
    return () => {
      room.stop();
      if (roomRef.current === room) roomRef.current = null;
    };
  }, [code, roomInstanceId, playerId, token, isDisplay]);
  const send = useCallback(
    (action: Action) => roomRef.current?.send(action) ?? null,
    [],
  );
  const retry = useCallback(() => roomRef.current?.retry(), []);
  return { ...snapshot, connection: snapshot.status, send, retry };
}
